import * as React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormHelperText,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { SvgIconComponent } from '@mui/icons-material';
import { formatDollarAmount } from '../../utils/global/helper';
import PricingTableRow from './PricingTableRow';
import {
  CheckoutFormValues,
  CustomCheckoutItem,
  TipState,
  tipSchema,
} from '../../types/appointments';
import FormSmallModal, { FormData } from '../global/FormSmallModal';
import { isDiscountAllowed, isTipDisplayed } from '../../helpers/payments';
import {
  SelectDiscountPayload,
  selectDiscountSchema,
} from '../../types/discounts';
import useGetDiscounts from '../../hooks/discounts/useGetDiscounts';
import { getDiscountLabel } from '../../helpers/discounts';
import useUser from '../../hooks/global/useUser';
import { getServiceName } from '../../helpers/appointments';
import { Appointment } from '../../interfaces';
import CheckoutItemsSearch from '../checkout/CheckoutItemsSearch';
import useGetCheckoutData from '../../hooks/checkouts/useGetCheckoutData';
import SplitPaymentFlow from '../payments/SplitPaymentFlow';
import { SplitPaymentOnFinishArgs } from '../../types/payments';

interface Props {
  appointment: Appointment | null;
  values: CheckoutFormValues;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined,
  ) => void;
  setTotalDue: React.Dispatch<React.SetStateAction<number | undefined>>;
  setIsTotalCoveredByGiftCard: React.Dispatch<React.SetStateAction<boolean>>;
  totalDue: number | undefined;
  isSplitPayment: boolean;
  onCloseSplitPayment: () => void;
  onCheckoutSuccess: (args: SplitPaymentOnFinishArgs) => void;
}

const getLineItemText = (text: string | number, isLoading?: boolean) =>
  isLoading ? '-' : text;

function FloatingButton({
  onClick,
  icon,
}: {
  onClick: () => void;
  icon: SvgIconComponent;
}) {
  const theme = useTheme();

  const Icon = icon;

  return (
    <Box
      component="span"
      sx={{
        position: 'absolute',
        right: '-55px',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
      }}>
      <Button
        sx={{
          padding: 0,
          minWidth: 0,
        }}
        onClick={onClick}>
        <Icon htmlColor={theme.palette.outlineGrey.main} />
      </Button>
    </Box>
  );
}

function LoadingContent({
  children,
  isLoading,
}: React.PropsWithChildren<{ isLoading?: boolean }>) {
  // eslint-disable-next-line react/jsx-no-useless-fragment
  if (!isLoading) return <>{children}</>;

  return (
    <Stack direction="row" justifyContent="end" alignItems="center">
      <CircularProgress size={16} color="primary" />
    </Stack>
  );
}

function CellAction({
  children,
  onClick,
}: React.PropsWithChildren & {
  onClick: () => void;
}) {
  const theme = useTheme();
  return (
    <Button
      variant="text"
      sx={{
        padding: 0,
        minWidth: 0,
      }}
      onClick={onClick}>
      <Typography variant="body2" sx={{ color: theme.palette.info.main }}>
        {children}
      </Typography>
    </Button>
  );
}

export default function PricingTable({
  appointment,
  values,
  setFieldValue,
  setTotalDue,
  setIsTotalCoveredByGiftCard,
  totalDue,
  isSplitPayment,
  onCloseSplitPayment,
  onCheckoutSuccess,
}: Props) {
  const { user } = useUser();

  const theme = useTheme();

  const { data: discounts = [] } = useGetDiscounts({
    activeOnly: true,
  });

  const customCheckoutItems = React.useMemo(
    () => values.customOrderItems ?? [],
    [values.customOrderItems],
  );

  const discountIsAllowed =
    !!discounts.length &&
    (!values.paymentMethod || isDiscountAllowed(values.paymentMethod));
  const selectedDiscount =
    discountIsAllowed && values.discountId
      ? discounts.find(discount => discount.id === values.discountId)
      : undefined;

  const {
    data: checkoutData,
    isFetching,
    isDebouncing,
    isPlaceholderData,
  } = useGetCheckoutData({
    appointment,
    values,
    discount: selectedDiscount ?? null,
  });
  const isLoading = isFetching || isDebouncing || isPlaceholderData;
  const {
    appointment: apptData,
    order: orderData,
    totalBeforeTaxesAndFeesCents,
    feeCents,
    totalPriceCents,
    hasTaxRate,
    discountAmountCents,
    giftCardAmountCents,
    isTotalCoveredByGiftCard,
  } = checkoutData ?? {};
  const price =
    ((apptData?.originalPriceCents || 0) +
      (orderData?.originalPriceCents || 0)) /
    100;
  const depositAmountPaid = apptData?.depositAmountPaidCents
    ? apptData.depositAmountPaidCents / 100
    : 0;
  const totalBeforeTaxAndFees = (totalBeforeTaxesAndFeesCents || 0) / 100;
  const serviceSalesTax = (apptData?.salesTaxCents || 0) / 100;
  const productSalesTax = (orderData?.salesTaxCents || 0) / 100;
  const customerServiceFee = (feeCents || 0) / 100;
  const tempTotalDue = (totalPriceCents || 0) / 100;
  const discountAmount = (discountAmountCents || 0) / 100;
  const giftCardAmount = (giftCardAmountCents || 0) / 100;
  const showSalesTax = values.paymentMethod !== 'CASH' || values.isCashTaxed;
  const serviceTaxRate = `${
    (user?.business?.businessTaxRates?.serviceTaxRate || 0) * 100
  }%`;
  const productTaxRate = `${
    (user?.business?.businessTaxRates?.productTaxRate || 0) * 100
  }%`;

  const displayTip = isTipDisplayed({
    values,
    appointment,
    user,
  });

  React.useEffect(() => {
    setTotalDue(tempTotalDue);
  }, [tempTotalDue, setTotalDue]);

  React.useEffect(() => {
    setIsTotalCoveredByGiftCard(!!isTotalCoveredByGiftCard);
  }, [isTotalCoveredByGiftCard, setFieldValue, setIsTotalCoveredByGiftCard]);

  const [form, setForm] = React.useState<FormData | null>(null);

  const initialValues = {
    tip: {
      tipAmt: null,
    } as TipState,
    discount: {
      discountId: selectedDiscount?.id || '',
    } as SelectDiscountPayload,
  };

  const tipForm: FormData<TipState> = {
    title: 'Enter tip amount',
    validationSchema: tipSchema,
    initialValues: initialValues.tip,
    inputRows: [
      [
        {
          inputType: 'number',
          label: 'Tip Amount',
          name: 'tipAmt',
        },
      ],
    ],
    onSubmit: async tipVal => {
      setFieldValue('tipAmt', tipVal.tipAmt ? Number(tipVal.tipAmt) : 0);
      setForm(null);
    },
  };

  const discountForm: FormData<SelectDiscountPayload> = {
    title: 'Select discount',
    initialValues: initialValues.discount,
    validationSchema: selectDiscountSchema,
    inputRows: [
      [
        {
          inputType: 'select',
          name: 'discountId',
          options: discounts.map(discount => ({
            value: discount.id,
            label: getDiscountLabel(discount),
          })),
          autoWidth: false,
          placeholder: 'Select discount',
          label: 'Discount',
        },
      ],
    ],
    onSubmit: async newValues => {
      setFieldValue('discountId', newValues.discountId);
      setForm(null);
    },
    submitButtonText: 'Apply',
  };

  const handleRemoveItem = (id: string) => {
    setFieldValue(
      'customOrderItems',
      customCheckoutItems?.filter(item => item.id !== id),
    );
  };

  const serviceName = appointment ? getServiceName(appointment, user) : '';

  return (
    <>
      <Box className="input-group">
        <Typography variant="h2">Transaction Details</Typography>
        <Box className="transaction-items-table" width="100%" overflow="auto">
          <Table style={{ width: '100%' }}>
            <TableHead>
              <TableRow
                sx={{
                  borderBottom: `1px solid ${theme.palette.darkGrey.main}`,
                }}>
                <TableCell
                  component="th"
                  scope="header"
                  sx={{
                    padding: 0,
                    border: 0,
                  }}>
                  <Typography variant="h4">Item</Typography>
                </TableCell>
                <TableCell
                  component="th"
                  scope="header"
                  sx={{
                    paddingY: 0,
                    border: 0,
                  }}>
                  <Typography variant="h4">Qty.</Typography>
                </TableCell>
                <TableCell
                  component="th"
                  scope="header"
                  sx={{
                    paddingY: 0,
                    border: 0,
                  }}>
                  <Typography variant="h4">Price</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {appointment && (
                <PricingTableRow
                  isQuantityDisabled
                  checkoutItem={{
                    id: Date.now().toString(),
                    name: serviceName,
                    price: values.price,
                    quantity: 1,
                  }}
                  onPriceChange={(value: number) =>
                    setFieldValue('price', value)
                  }
                  disabled={values.paymentMethod === 'FREE'}
                />
              )}
              {customCheckoutItems &&
                customCheckoutItems.map((item: CustomCheckoutItem) => (
                  <PricingTableRow
                    key={item.id}
                    checkoutItem={item}
                    onPriceChange={(value: number) => {
                      // Change the items price to the new value
                      setFieldValue(
                        'customOrderItems',
                        (customCheckoutItems ?? []).map(prevItem =>
                          prevItem.id === item.id
                            ? { ...prevItem, price: value }
                            : prevItem,
                        ),
                      );
                    }}
                    onQuantityChange={(value: number) => {
                      // Change the items price to the new value
                      setFieldValue(
                        'customOrderItems',
                        (customCheckoutItems ?? []).map(prevItem =>
                          prevItem.id === item.id
                            ? { ...prevItem, quantity: value }
                            : prevItem,
                        ),
                      );
                    }}
                    onRemove={() => handleRemoveItem(item.id)}
                  />
                ))}
            </TableBody>
          </Table>
        </Box>

        {values.paymentMethod !== 'FREE' && (
          <CheckoutItemsSearch appointment={appointment} />
        )}

        <Box
          className="pricing-summary-table"
          sx={{
            '& .MuiTableRow-root': {
              borderBottom: `1px solid ${theme.palette.lightGrey.main}`,
            },
            '& .MuiTableCell-body': {
              paddingX: 0,
              border: 'none',
            },
            maxWidth: '400px',
            border: `1px solid ${theme.palette.lightGrey.main}`,
            borderRadius: 1,
            padding: 3,
          }}>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell>
                  <Typography variant="body2">Price</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" style={{ textAlign: 'right' }}>
                    {getLineItemText(
                      formatDollarAmount(price + depositAmountPaid),
                      isLoading,
                    )}
                  </Typography>
                </TableCell>
              </TableRow>
              {appointment && !!depositAmountPaid && (
                <TableRow>
                  <TableCell sx={{ display: 'flex', gap: 1 }}>
                    <Typography variant="body2">Deposit</Typography>
                    <Typography
                      variant="body2"
                      style={{ marginRight: 5 }}
                      color="secondary">
                      (paid)
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      style={{ textAlign: 'right' }}
                      color={theme.palette.primary.main}>
                      {getLineItemText(
                        formatDollarAmount(depositAmountPaid),
                        isLoading,
                      )}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {displayTip && !!values.tipAmt && (
                <TableRow>
                  <TableCell>
                    <Typography variant="body2">Tip added</Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2" style={{ textAlign: 'right' }}>
                      {formatDollarAmount(values.tipAmt)}
                    </Typography>
                  </TableCell>

                  <FloatingButton
                    icon={DeleteIcon}
                    onClick={() => setFieldValue('tipAmt', 0)}
                  />
                </TableRow>
              )}

              <TableRow>
                <TableCell>
                  <Typography variant="h4">Subtotal</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="h4" style={{ textAlign: 'right' }}>
                    {getLineItemText(
                      totalBeforeTaxAndFees && totalBeforeTaxAndFees > 0
                        ? formatDollarAmount(totalBeforeTaxAndFees)
                        : '$0.00',
                      isLoading,
                    )}
                  </Typography>
                </TableCell>
              </TableRow>

              {discountIsAllowed && (
                <>
                  {!selectedDiscount && (
                    <TableRow>
                      <TableCell>
                        <CellAction onClick={() => setForm(discountForm)}>
                          Add discount
                        </CellAction>
                      </TableCell>
                    </TableRow>
                  )}
                  {selectedDiscount && (
                    <TableRow>
                      <TableCell>
                        <Typography variant="body2">
                          Discount - {getDiscountLabel(selectedDiscount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          style={{ textAlign: 'right' }}
                          color={theme.palette.primary.main}>
                          {getLineItemText(
                            `-${formatDollarAmount(discountAmount)}`,
                            isLoading,
                          )}
                        </Typography>
                      </TableCell>
                      {!isLoading && (
                        <FloatingButton
                          icon={DeleteIcon}
                          onClick={() => setFieldValue('discountId', undefined)}
                        />
                      )}
                    </TableRow>
                  )}
                </>
              )}

              {displayTip && !values.tipAmt && (
                <TableRow>
                  <TableCell>
                    <CellAction onClick={() => setForm(tipForm)}>
                      Add tip
                    </CellAction>
                  </TableCell>
                </TableRow>
              )}

              {hasTaxRate &&
                values.paymentMethod === 'CASH' &&
                !values.isCashTaxed && (
                  <TableRow>
                    <TableCell>
                      <CellAction
                        onClick={() => setFieldValue('isCashTaxed', true)}>
                        Add sales tax
                      </CellAction>
                    </TableCell>
                  </TableRow>
                )}

              {!!serviceSalesTax && showSalesTax && (
                <TableRow>
                  <TableCell>
                    <Typography variant="body2">
                      Service sales tax ({serviceTaxRate})
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" style={{ textAlign: 'right' }}>
                      {getLineItemText(
                        formatDollarAmount(serviceSalesTax),
                        isLoading,
                      )}
                    </Typography>
                  </TableCell>
                  {values.paymentMethod === 'CASH' && values.isCashTaxed && (
                    <FloatingButton
                      icon={DeleteIcon}
                      onClick={() => setFieldValue('isCashTaxed', false)}
                    />
                  )}
                </TableRow>
              )}

              {!!productSalesTax && showSalesTax && (
                <TableRow>
                  <TableCell>
                    <Typography variant="body2">
                      Product sales tax ({productTaxRate})
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" style={{ textAlign: 'right' }}>
                      {getLineItemText(
                        formatDollarAmount(productSalesTax),
                        isLoading,
                      )}
                    </Typography>
                  </TableCell>
                  {values.paymentMethod === 'CASH' && values.isCashTaxed && (
                    <FloatingButton
                      icon={DeleteIcon}
                      onClick={() => setFieldValue('isCashTaxed', false)}
                    />
                  )}
                </TableRow>
              )}

              {!!customerServiceFee && (
                <TableRow>
                  <TableCell>
                    <Typography variant="body2">Convenience fee</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" style={{ textAlign: 'right' }}>
                      {getLineItemText(
                        formatDollarAmount(customerServiceFee),
                        isLoading,
                      )}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              <TableRow sx={{ borderBottom: 'none !important' }}>
                <TableCell>
                  <Typography variant="h3" color="black">
                    Total Due
                  </Typography>
                </TableCell>
                <TableCell>
                  <LoadingContent isLoading={isLoading}>
                    <Typography
                      variant="h3"
                      style={{ textAlign: 'right' }}
                      color="black">
                      {totalDue && totalDue > 0
                        ? formatDollarAmount(totalDue)
                        : totalDue === 0 &&
                          isTotalCoveredByGiftCard &&
                          giftCardAmount > 0
                        ? formatDollarAmount(giftCardAmount)
                        : '$0.00'}
                    </Typography>
                  </LoadingContent>
                </TableCell>
              </TableRow>

              {(!totalDue || totalDue <= 0) &&
                values.paymentMethod !== 'FREE' &&
                !isTotalCoveredByGiftCard && (
                  <TableRow sx={{ borderBottom: 'none !important' }}>
                    <TableCell colSpan={2}>
                      <FormHelperText
                        sx={{
                          textAlign: 'right',
                          width: '100%',
                          color: theme.palette.error.main,
                        }}>
                        Enter a price greater than{' '}
                        {formatDollarAmount(appointment?.depositPrice)}
                      </FormHelperText>
                    </TableCell>
                  </TableRow>
                )}
            </TableBody>
          </Table>
        </Box>
        <FormSmallModal
          open={!!form}
          onClose={() => setForm(null)}
          form={form}
        />
      </Box>
      {!!checkoutData && (
        <SplitPaymentFlow
          isOpen={isSplitPayment}
          onFinish={onCheckoutSuccess}
          onClose={onCloseSplitPayment}
          checkoutData={checkoutData}
        />
      )}
    </>
  );
}
